const GATEWAY = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000'

// The server does all the money maths and returns the whole post-discount
// breakdown; the UI only renders these values.
export interface CouponValidation {
  valid:      boolean
  discount:   number   // INR off the GST-inclusive cart total
  finalTotal: number   // GST-inclusive amount payable after the discount
  subtotal:   number   // base price excluding GST (display only)
  tax:        number   // GST contained within finalTotal (display only)
  message:    string
}

// Validate a coupon against the current GST-inclusive cart total. The user is
// derived from the auth token on the gateway (x-user-id), so no userId is sent
// from the client.
export async function validateCoupon(
  code:      string,
  cartTotal: number,
  token:     string,
): Promise<CouponValidation> {
  const res = await fetch(`${GATEWAY}/api/coupons/validate`, {
    method:  'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify({ code: code.trim().toUpperCase(), cartTotal }),
  })

  const data = await res.json()
  if (!res.ok) {
    throw new Error(data.message || data.error || 'Could not validate coupon')
  }
  return data as CouponValidation
}

// ── Advertised offers ─────────────────────────────────────────────────────────

/** A coupon the customer is allowed to see. Never carries usage counts. */
export interface AvailableCoupon {
  code:            string
  description:     string
  discount_type:   'percentage' | 'fixed'
  discount_value:  number
  min_order_value: number
  expiry_date:     string | null
}

/**
 * Offers this signed-in customer can actually use — public, active, unexpired
 * and not already spent by them. The server does the eligibility filtering; the
 * list is a shortcut for typing a code, never a promise (applying still goes
 * through validateCoupon).
 */
export async function getAvailableCoupons(token: string): Promise<AvailableCoupon[]> {
  const res = await fetch(`${GATEWAY}/api/coupons/available`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  })

  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Could not load offers')
  return Array.isArray(data.coupons) ? data.coupons : []
}

/** What the landing page ticker shows for one offer. */
export interface BannerCoupon {
  code:           string
  description:    string
  discount_type:  'percentage' | 'fixed'
  discount_value: number
}

/**
 * Offers cleared for the announcement ticker. Anonymous — this runs on the
 * landing page before sign-in — and deliberately forgiving: the ticker has
 * default copy to fall back on, so a failure here returns an empty list rather
 * than throwing into a first paint.
 */
export async function getBannerCoupons(): Promise<BannerCoupon[]> {
  try {
    const res = await fetch(`${GATEWAY}/api/coupons/banner`)
    if (!res.ok) return []
    const data = await res.json()
    return Array.isArray(data.coupons) ? data.coupons : []
  } catch {
    return []
  }
}
